import { Ship, Plane, Truck, PackageCheck, Warehouse, Package, FileText, ThermometerSun, ThermometerSnowflake, FlameKindling, ShieldCheck, Stamp, Boxes, ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Eyebrow, Reveal, Rich } from "./Motion";
import { scrollToId } from "./SmoothScroll";

const Card = ({ className = "", children, testid, img, alt, chip }) => (
  <Reveal className={className}>
    <div data-testid={testid}
      className="group relative h-full min-h-[260px] rounded-[6px] overflow-hidden border border-[rgba(16,16,20,0.12)] bg-white shadow-[0_18px_50px_-30px_rgba(16,16,20,0.25)]">
      {img && (
        <>
          <img src={img} alt={alt} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#16265c]/85 via-[#16265c]/45 to-transparent" />
        </>
      )}
      <div className="relative h-full p-6 lg:p-8 flex flex-col">{children}</div>
      {chip && <span className="absolute top-4 left-4 mono text-[10px] uppercase bg-[var(--fux)] text-white px-2.5 py-1 rounded-sm">{chip}</span>}
    </div>
  </Reveal>
);

const MODE_ICONS = [Ship, Plane, Truck, PackageCheck];
const MAINS = [
  { Icon: ThermometerSun, c: "#b08d1f" },
  { Icon: ThermometerSnowflake, c: "#16265c" },
  { Icon: FlameKindling, c: "#c4006b" },
];

export const Logistics = () => {
  const { t } = useLang();
  const L = t.logistics;
  return (
    <section id="logistics" data-testid="logistics-section" className="relative py-24 lg:py-40 bg-[var(--bone)]">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 mb-14 lg:mb-20">
          <div className="lg:col-span-7">
            <Eyebrow className="!text-[#b08d1f] [&>span]:!bg-[#b08d1f]">{L.eyebrow}</Eyebrow>
            <Reveal><h2 data-testid="logistics-title" className="mt-8 display text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-[-0.01em]"><Rich text={L.title} /></h2></Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-4 lg:col-start-9 self-end"><p className="text-base lg:text-lg text-[rgba(16,16,20,0.75)] leading-relaxed">{L.intro}</p></Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 lg:auto-rows-[290px] gap-4">
          <Card testid="bento-modes" className="md:col-span-2 lg:col-span-8 lg:row-span-2" img="/img/ship.jpg" alt="Nave portacontainer al tramonto">
            <span className="mono text-[11px] uppercase text-[var(--amber-2)]">{L.modesTitle}</span>
            <div className="mt-auto pt-40 lg:pt-0">
              <p className="max-w-lg text-lg lg:text-2xl font-semibold leading-snug tracking-tight text-[var(--bone)]">{L.modesDesc}</p>
              <ul className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {L.modes.map((m, i) => {
                  const Icon = MODE_ICONS[i];
                  return (
                    <li key={m} data-testid={`mode-${i}`} className="flex items-center gap-2 rounded-full border border-[rgba(246,241,232,0.3)] bg-[rgba(16,16,20,0.55)] backdrop-blur-md px-4 py-2.5 text-sm text-[var(--bone)] hover:bg-[var(--fux)] hover:text-white hover:border-[var(--fux)] transition-colors duration-300">
                      <Icon className="w-4 h-4" />{m}
                    </li>
                  );
                })}
              </ul>
            </div>
          </Card>

          <Card testid="bento-goods" className="md:col-span-2 lg:col-span-4 lg:row-span-2">
            <span className="mono text-[11px] uppercase text-[#b08d1f]">{L.goodsTitle}</span>
            <p aria-hidden="true" className="display text-4xl lg:text-5xl mt-4 leading-none text-[rgba(16,16,20,0.9)]">−18°<span className="serif text-[var(--fux)] px-1">/</span>+25°</p>
            <p className="mono text-[11px] uppercase text-[rgba(16,16,20,0.6)] mt-2">ADR · IMDG · IATA · CLASS 1–9</p>
            <ul className="mt-auto pt-4">
              {L.goods.map((g, i) => {
                const { Icon, c } = MAINS[i];
                return (
                  <li key={i} data-testid={`goods-${i}`} className="py-3 border-t border-[rgba(16,16,20,0.1)] flex gap-3.5">
                    <span className="mt-0.5 w-10 h-10 shrink-0 rounded-full grid place-items-center border" style={{ background: `${c}0d`, borderColor: `${c}3d`, color: c }}><Icon strokeWidth={1.5} className="w-[19px] h-[19px]" /></span>
                    <span><span className="block text-[15px] lg:text-base font-semibold tracking-tight leading-tight">{g.t}</span><span className="block text-[12.5px] text-[rgba(16,16,20,0.7)] leading-snug">{g.d}</span></span>
                  </li>
                );
              })}
            </ul>
            <p className="mono text-[10px] uppercase text-[#b08d1f] mt-4">{L.subsLabel}</p>
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {L.subs.map((s, i) => (
                <li key={i} data-testid={`goods-sub-${i}`} className="mono text-[10px] uppercase px-2 py-1 rounded-full border border-[rgba(16,16,20,0.18)] text-[rgba(16,16,20,0.75)]">{s}</li>
              ))}
            </ul>
          </Card>

          <Card testid="bento-air" className="lg:col-span-4" img="/img/plane.jpg" alt="Aereo cargo al tramonto" chip="Air cargo">
            <Plane className="w-6 h-6 text-[var(--amber-2)]" />
            <p className="mono text-[11px] uppercase text-[rgba(246,241,232,0.85)] mt-auto">IATA · AWB · EXPRESS</p>
          </Card>

          <Card testid="bento-warehousing" className="lg:col-span-4" img="/img/warehouse.jpg" alt="Magazzino moderno">
            <Warehouse className="w-6 h-6 text-[var(--amber-2)]" />
            <div className="mt-auto"><h3 className="text-2xl font-bold tracking-tight text-[var(--bone)]">{L.warehousing.t}</h3><p className="text-sm text-[rgba(246,241,232,0.85)] mt-1">{L.warehousing.d}</p></div>
          </Card>

          <Card testid="bento-packaging" className="lg:col-span-4" img="/img/cold.jpg" alt="Merce imballata su pallet in magazzino">
            <Package className="w-6 h-6 text-[var(--amber-2)]" />
            <div className="mt-auto"><h3 className="text-2xl font-bold tracking-tight text-[var(--bone)]">{L.packaging.t}</h3><p className="text-sm text-[rgba(246,241,232,0.85)] mt-1">{L.packaging.d}</p></div>
          </Card>

          <Card testid="bento-customs" className="md:col-span-2 lg:col-span-4" img="/img/customs.jpg" alt="Timbro su documenti doganali">
            <FileText className="w-6 h-6 text-[var(--amber-2)]" />
            <p className="mono text-[11px] uppercase text-[rgba(246,241,232,0.85)] mt-auto leading-6">B/L · AWB · CMR · EUR.1 · DAE · T1 · INVOICE · PACKING LIST</p>
            <div className="mt-3"><h3 className="text-2xl font-bold tracking-tight text-[var(--bone)]">{L.customs.t}</h3><p className="text-sm text-[rgba(246,241,232,0.85)] mt-1">{L.customs.d}</p></div>
          </Card>

          <Reveal className="md:col-span-2 lg:col-span-8">
            <button data-testid="bento-cta" onClick={() => scrollToId("contact")}
              className="group relative w-full h-full min-h-[260px] rounded-[6px] overflow-hidden bg-[var(--fux)] text-white p-6 lg:p-8 flex items-end">
              <ArrowUpRight strokeWidth={0.8} className="absolute -right-10 -top-10 w-64 h-64 opacity-20 transition-transform duration-700 group-hover:rotate-45 group-hover:scale-110" />
              <span className="relative">
                <span className="mono text-[11px] uppercase opacity-80">MF · {L.eyebrow.split("—")[0]}</span>
                <span className="display block text-3xl lg:text-5xl mt-3">{t.nav.cta}</span>
              </span>
            </button>
          </Reveal>

          <Reveal className="lg:col-span-4">
            <div data-testid="bento-insurance" className="group relative h-full min-h-[240px] rounded-[6px] overflow-hidden border border-[rgba(16,16,20,0.12)] bg-white p-6 lg:p-8 flex flex-col shadow-[0_18px_50px_-30px_rgba(22,38,92,0.25)]">
              <ShieldCheck className="w-6 h-6 text-[#b08d1f]" />
              <div className="mt-auto"><h3 className="text-2xl font-bold tracking-tight">{L.insurance.t}</h3><p className="text-sm text-[rgba(16,16,20,0.7)] mt-1">{L.insurance.d}</p></div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-4">
            <div data-testid="bento-docs" className="group relative h-full min-h-[240px] rounded-[6px] overflow-hidden border border-[rgba(16,16,20,0.12)] bg-white p-6 lg:p-8 flex flex-col shadow-[0_18px_50px_-30px_rgba(22,38,92,0.25)]">
              <Stamp className="w-6 h-6 text-[var(--fux)]" />
              <div className="mt-auto"><h3 className="text-2xl font-bold tracking-tight">{L.docsPermits.t}</h3><p className="text-sm text-[rgba(16,16,20,0.7)] mt-1">{L.docsPermits.d}</p></div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-4">
            <div data-testid="bento-pallet" className="group relative h-full min-h-[240px] rounded-[6px] overflow-hidden border border-[rgba(16,16,20,0.12)] bg-white p-6 lg:p-8 flex flex-col shadow-[0_18px_50px_-30px_rgba(22,38,92,0.25)]">
              <Boxes className="w-6 h-6 text-[#b08d1f]" />
              <div className="mt-auto"><h3 className="text-2xl font-bold tracking-tight">{L.palletControl.t}</h3><p className="text-sm text-[rgba(16,16,20,0.7)] mt-1">{L.palletControl.d}</p></div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
